import { useCallback, useEffect, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import type moment from 'moment'

import TextButton from 'LuxKit/Button/TextButton'
import ResponsiveDropdownSheet from 'tripPlanner/components/Common/Dropdowns/ResponsiveDropdownSheet'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import {
  INTL_SHORT_DAY_NAME_DAY_MONTH,
  INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR,
} from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import DateRangePicker from 'tripPlanner/Tripkit/Common/DateRangePicker'

interface Props {
  triggerRef: React.RefObject<HTMLElement | null>
  isOpen: boolean
  startDate?: moment.Moment
  endDate?: moment.Moment
  onDatesChange: (dates: {
    startDate: moment.Moment
    endDate?: moment.Moment | null
  }) => void
  onClear?: () => void
  onClose?: () => void
  startLabel?: string
  endLabel?: string
  rangeType: 'days' | 'nights'
  initDate?: moment.Moment
  portaled?: boolean
  placement?: 'top' | 'bottom'
}

function DateRangeDropdown({
  triggerRef,
  isOpen,
  startDate: startDateProp,
  endDate: endDateProp,
  onDatesChange,
  onClear,
  onClose,
  startLabel,
  endLabel,
  rangeType,
  initDate,
  portaled,
  placement = 'bottom',
}: Props) {
  const intl = useIntl()

  const [dates, setDates] = useState<{ startDate?: moment.Moment, endDate?: moment.Moment }>({
    startDate: startDateProp,
    endDate: endDateProp,
  })
  const { startDate, endDate } = dates

  const hasDates = !!(startDate && endDate)

  const dateRangeLabel = (startDate && endDate) ? (
    rangeType === 'days'
      ? intl.formatMessage(
          {
            id: '77LSxnWLWz2C3q1PpTPT',
            defaultMessage: '({count, plural, one {# day} other {# days}})',
            description: 'Number of days in selected trip date range in the trip planner',
          },
          { count: endDate.diff(startDate, 'days') + 1 },
        )
      : intl.formatMessage(
          {
            id: 'hiWxgUjwNAzAv5-Zr8SU',
            defaultMessage: '({count, plural, one {# night} other {# nights}})',
            description: 'Number of nights in selected trip date range in the trip planner',
          },
          { count: endDate.diff(startDate, 'days') },
        )
  ) : undefined

  useEffect(() => {
    setDates({ startDate: startDateProp, endDate: endDateProp })
  }, [startDateProp, endDateProp])

  const apply = useCallback(() => {
    if (dates.startDate) {
      onDatesChange(dates as { startDate: moment.Moment, endDate?: moment.Moment })
    }
  }, [dates, onDatesChange])

  const isDesktop = useScreenSizeUp('desktop')

  return (
    <ResponsiveDropdownSheet
      triggerRef={triggerRef}
      anchorRef={triggerRef}
      size="L"
      open={isOpen}
      placement={placement}
      onClose={onClose}
      crossAxisShift
      portaled={portaled}
    >
      <DateRangePicker
        onDatesChange={setDates}
        startDate={startDate}
        endDate={endDate}
        startLabel={startLabel}
        endLabel={endLabel}
        initDate={initDate}
        type={isDesktop ? 'dual' : 'single'}
        allowSameDay={false}
        showTripDates
      />
      {!!onClear && (
        <Group direction="horizontal" horizontalAlign="end" gap={16}>
          {hasDates && (
            <Group direction="horizontal" gap={8} verticalAlign="center">
              <Group direction="horizontal" gap={4} verticalAlign="center">
                <BodyText variant="medium" weight="bold">
                  {intlDateFormatter(startDate, INTL_SHORT_DAY_NAME_DAY_MONTH, intl.locale)}
                </BodyText>

                <LineArrowRightIcon size="S" />

                <BodyText variant="medium" weight="bold">
                  {intlDateFormatter(endDate, INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR, intl.locale)}
                </BodyText>
              </Group>

              <BodyText variant="medium">
                {dateRangeLabel}
              </BodyText>
            </Group>
          )}
          <TextButton kind="tertiary" onClick={onClear} horizontalOutdent="end">
            {intl.formatMessage({
              id: 'cStfuhd8mO-NslpVA_mD',
              defaultMessage: 'Clear dates',
              description: 'Button to clear selected date range in trip planner',
            })}
          </TextButton>
          <TextButton kind="primary" onClick={apply} disabled={!startDate}>
            {intl.formatMessage({
              id: 'AOdjW-PWSg5O8TKxAjbE',
              defaultMessage: 'Apply',
              description: 'Button to apply selected date range in trip planner',
            })}
          </TextButton>
        </Group>
      )}
    </ResponsiveDropdownSheet>
  )
}

export default DateRangeDropdown
